import { Component } from '@theme/component';

/**
 * @typedef {Object} SfHeaderDrawerRefs
 * @property {HTMLButtonElement} closeButton - Close button
 * @property {HTMLButtonElement[]} l1Item - L1 navigation buttons
 * @property {HTMLElement[]} l3Panel - L3 content panels
 */

/**
 * Mobile drawer component for Sterling Forever header.
 *
 * Manages full-screen drawer with two-column L1/L3 navigation,
 * focus trap, keyboard dismiss, and body scroll lock.
 *
 * @extends {Component<SfHeaderDrawerRefs>}
 */
class SfHeaderDrawer extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#handleKeydown);
  }

  /**
   * Opens the drawer. Called via on:click="#sf-header-drawer/open".
   */
  open() {
    this.dataset.open = 'true';
    this.setAttribute('aria-hidden', 'false');
    this.#setTriggerExpanded(true);
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      this.refs.closeButton?.focus();
    });

    // Select first L1 with a submenu by default
    const firstWithPanel = this.refs.l1Item?.find((item) => !item.dataset.url);
    if (firstWithPanel) {
      this.#activatePanel(parseInt(firstWithPanel.dataset.index ?? '', 10));
    }
  }

  /**
   * Keeps the hamburger's aria-expanded in step with the drawer.
   * The drawer itself was already operable by keyboard; what was missing was any
   * announcement of whether it is open (WCAG 4.1.2).
   *
   * @param {boolean} expanded
   */
  #setTriggerExpanded(expanded) {
    for (const trigger of document.querySelectorAll('[data-drawer-trigger]')) {
      trigger.setAttribute('aria-expanded', String(expanded));
    }
  }

  /**
   * Closes the drawer. Called via on:click="/close".
   */
  close() {
    this.dataset.open = 'false';
    this.setAttribute('aria-hidden', 'true');
    this.#setTriggerExpanded(false);
    document.body.style.overflow = '';

    const trigger = document.querySelector('[data-drawer-trigger]');
    if (trigger instanceof HTMLElement) {
      trigger.focus();
    }
  }

  /**
   * Selects an L1 item and shows matching L3 panel.
   * Called via on:click="/selectL1" on L1 buttons.
   *
   * @param {PointerEvent} event
   */
  selectL1(event) {
    const target = event.target?.closest('.sf-drawer__l1-item');
    if (!(target instanceof HTMLElement)) return;

    const index = parseInt(target.dataset.index ?? '', 10);
    if (Number.isNaN(index)) return;

    this.#activatePanel(index);

    if (target.dataset.url) {
      window.location.href = target.dataset.url;
    }
  }

  /**
   * Activates a specific L1/L3 panel by index.
   *
   * @param {number} index
   */
  #activatePanel(index) {
    const items = this.refs.l1Item;
    const panels = this.refs.l3Panel;
    if (!items || !panels) return;

    for (const item of items) {
      item.dataset.active = String(parseInt(item.dataset.index ?? '', 10) === index);
    }

    for (const panel of panels) {
      panel.setAttribute('aria-hidden', String(parseInt(panel.dataset.index ?? '', 10) !== index));
    }
  }

  /**
   * Closes drawer on Escape key.
   *
   * @type {(event: KeyboardEvent) => void}
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.dataset.open === 'true') {
      this.close();
    }
  };
}

if (!customElements.get('sf-header-drawer')) {
  customElements.define('sf-header-drawer', SfHeaderDrawer);
}
